/// <reference path="../../../../typings/index.d.ts" />

module Meevo.Plugins.Interop {
    import DocsAndFormsInteropService = Meevo.Plugins.DocsAndForms.DocsAndFormsInteropService;
    declare const postRobot: any;

    export class InteropService {
        private interopDomainUrl: string;
        private registeredWindows: { id: string, window: Window }[] = [];

        /**
         * Sticky per-context buffer of host-provided params, keyed by interop state name.
         * Survives iframe re-mounts so a convo/menu reopen replays the most recent params.
         * Cleared by removeWindow() (tab close) and clearContextParams() (explicit reset).
         */
        private contextParamsBuffer: { [stateName: string]: any } = {};

        /**
         * The child Window that announced readiness (`mi:ChildReady`) per interop
         * state name. Readiness is keyed by *Window identity*, not by a boolean:
         * that is the only way to tell "the iframe was replaced, its listeners are
         * gone" apart from "the same live iframe was simply re-registered".
         *
         * A boolean flag could not make that distinction, and the previous
         * implementation therefore cleared readiness on the FIRST registerWindow()
         * call for every context (prior entry was null, so `prior !== window` was
         * always true). Because registerWindow() runs behind a timer in
         * InteropController.registerIframe(), any child that announced readiness
         * before that timer fired had its readiness silently destroyed — and the
         * child never re-announces on its own, so every later pushParamsToContext
         * for that tab buffered forever and delivered nothing. (#121880)
         *
         * Storing the Window also lets flushParamsToChild() deliver without waiting
         * for registerWindow() at all: ChildReady's `event.source` *is* the iframe's
         * contentWindow, so it is a valid post-robot target the moment it arrives.
         */
        private readyWindows: { [stateName: string]: Window } = {};

        /**
         * Per-state buffer of querystring params to append to the iframe URL the
         * next time InteropController creates/refreshes the iframe for that state.
         * Consumed (read-and-cleared) by consumeQueryParams() — so params only
         * appear on one iframe creation cycle, not on every re-mount.
         * Cleared by removeWindow() for consistency.
         */
        private queryParamsBuffer: { [stateName: string]: Record<string, string> } = {};

        /**
         * Monotonic stamp applied to each sendContextParams() request so the child can
         * distinguish a new request from a repeat delivery of one it already honoured.
         */
        private contextParamsSeq = 0;

        public get interopRegisteredWindows(): { id: string, window: Window }[] {
            return this.registeredWindows;
        }
        private get interopDomain() {
            return {
                domain: this.interopDomainUrl
            }
        }

        constructor(
            protected $cookies: angular.cookies.ICookiesService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected $rootScope: angular.IRootScopeService,
            protected $state: angular.ui.IStateService,
            docsAndFormsInteropService: DocsAndFormsInteropService,
            private busyService: Meevo.Components.Busy.IBusyService,
            private $translate: angular.translate.ITranslateService,
            private businessInfoDataService: Meevo.Components.Common.IBusinessInfoDataService) {

            this.interopDomainUrl = appSettings.INTEROP_URL;

            docsAndFormsInteropService.registerEvents(postRobot, this.interopDomain);

            postRobot.on(InteropActions.RefreshLockoutLastEventTime, this.interopDomain, event => {
                $rootScope.$broadcast(Meevo.Components.Common.Messages.REFRESH_LOCKOUT_LAST_EVENT_TIME);
            });

            // A Meevo shell v2 settings screen saved a business-info change and wants
            // the host to reflect it live (no logout/login). Re-pull and patch caches.
            // Returning the promise lets the child await the refresh before continuing.
            postRobot.on(InteropActions.RefreshBusinessInfoSettings, this.interopDomain, event => {
                return this.refreshBusinessInfoSettings();
            });

            postRobot.on(InteropActions.GetSession, this.interopDomain, event => {
                return this.getSession();
            });

            postRobot.on(InteropActions.ShowModalAlert, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.alert(data.contentKey, data.translationMode, data.canShowMultiple, data.userOptions, data.headerContentKey);
            });

            postRobot.on(InteropActions.ShowModalCustom, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.showCustom({
                    templateUrl: data.options.templateUrl,
                    controller: data.options.controller,
                    controllerAs: data.options.controllerAs,
                    bindToController: data.options.bindToController,
                    locals: data.options.locals
                });
            })

            postRobot.on(InteropActions.StartBusyService, this.interopDomain, event => {
                const data = event.data;
                // seem to always need to have the timeout to get it to show
                this.busyService.start(data.messageKey, data.highVisibility, true);
            });

            postRobot.on(InteropActions.StopBusyService, this.interopDomain, event => {
                // seem to always need to have the timeout to get it to hide
                this.busyService.stop(true);
            });

            postRobot.on(InteropActions.Translate, this.interopDomain, event => {
                return this.$translate.instant(event.data);
            });

            postRobot.on(InteropActions.ShowModalConfirm, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.confirm(data.contentKey, data.translationMode, data.canShowMultiple, data.userOptions);
            });

            postRobot.on(InteropActions.ShowModalConfirmHandler, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.confirmHandler(data.contentKey, data.translationMode, data.canShowMultiple);
            });

            postRobot.on(InteropActions.ShowModalRedAlertConfirm, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.redAlertConfirm(data.contentKey, data.translationMode, data.canShowMultiple, data.userOptions);
            });

            postRobot.on(InteropActions.ShowModalInfo, this.interopDomain, event => {
                const data = event.data;
                return this.modalService.info(data.contentKey, data.translationMode, data.canShowMultiple, data.userOptions);
            });

            postRobot.on(InteropActions.CloseContext, this.interopDomain, event => {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.CLOSE_CONTEXT, this.$state.current, null);
            });

            postRobot.on(InteropActions.GoToState, this.interopDomain, (event) => {
                const { data } = event;

                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, data);
            });

            postRobot.on(InteropActions.CloseIntakeFormDesigner, this.interopDomain, event => {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.CLOSE_INTAKE_FORM_DESIGNER);
            });

            postRobot.on(InteropActions.ShowAuditHistoryModal, this.interopDomain, event => {
                const { data } = event;
                this.showAuditHistoryModal(data);
            });

            postRobot.on(InteropActions.ReturnFromOnHold, this.interopDomain, event => {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                    stateName: "register",
                    stateParams: {
                        action: Meevo.Components.Common.Messages.RETURN_FROM_ON_HOLD,
                        id: event.data
                    }
                });
            });

            postRobot.on(InteropActions.AddPaymentRequestToSale, this.interopDomain, event => {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                    stateName: "register",
                    stateParams: {
                        action: Meevo.Components.Common.Messages.ADD_PAYMENT_REQUEST_TO_SALE,
                        ...event.data
                    }
                });
            });

            postRobot.on(InteropActions.OpenClientChart, this.interopDomain, event => {
                let serviceIds = event.data.appointmentServiceIds;

                if (!serviceIds && event.data.clientChartAppointmentServices) {
                    serviceIds = event.data.clientChartAppointmentServices.map(s => s.appointServiceId).join(',');
                }

                const params = {
                    id: event.data.clientId,
                    period: moment(event.data.visitDate).format('YYYYMMDD'), 
                    mode: event.data.mode,
                    clientChartId: event.data.clientChartId ? event.data.clientChartId : 0,
                    locationId: event.data.locationId,
                    chartdefinitionId: event.data.chartDefinitionId,
                    chartVersionId: event.data.chartVersionId,
                    clientChartStatus: event.data.clientChartStatus,
                    appointmentServiceIds: serviceIds,
                    clientDisplayName: event.data.clientDisplayName,
                    sourceModule: event.data.sourceModule
                };
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                    stateName: this.appSettings.ROUTE_NAMES.client,
                    stateParams: {
                        actionState: Meevo.Model.MaintenanceFormState.Readonly,
                        targetState: Meevo.Model.MaintenanceFormVisualState.Detail,
                        initialEditSection: "SECTION_CLIENT_CHARTS",
                        callbackState: event.data.sourceModule,
                        ...params
                    }
                });
            });

            postRobot.on(InteropActions.SetMaintenanceFormState, this.interopDomain, event => {
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.SET_MAINTENANCE_FORM_STATE, event);
            });

            postRobot.on(InteropActions.GetAppSettings, this.interopDomain, event => {
                return this.getAppSettings();
            });

            postRobot.on(InteropActions.GetWalkMeAppSettings, this.interopDomain, event => {
                return this.getWalkmeAppSettings();
            });

            postRobot.on(InteropActions.GetTippyServiceUrl, this.interopDomain, event => {
                return this.getTippyServiceUrl();
            });

            postRobot.on(InteropActions.GetPendoData, this.interopDomain, event => {
                return this.getPendoData();
            });

            $rootScope.$on(Meevo.Components.Common.Messages.AUTHENTICATION_SUCCESS, (event: angular.IAngularEvent, args: Meevo.Components.Common.IIdentity) => {
                for (const win of this.registeredWindows) {
                    postRobot.send(win.window, InteropActions.RefreshSession, win.id, this.interopDomain)
                        .then(event => console.debug('[Meevo] send: ', event.data.message))
                        .catch(error => {
                            var contextItem = <IContext>this.$state.get(win.id);
                            this.$rootScope.$broadcast(Meevo.Components.Common.Messages.CLOSE_CONTEXT, contextItem, null);
                            this.removeWindow(win.id);
                            console.warn('Meevo Error', error.stack)
                        });

                }
            });

            postRobot.on(InteropActions.SendMessage, this.interopDomain, (event) => {
                const { data } = event;

                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.SEND_MESSAGE, data);
            });

            // Handshake: child iframes emit ChildReady once their post-robot listeners
            // are registered. Receiving this signal is the only thing that authorises
            // flushing buffered context params to that iframe — no timeouts, no retries.
            postRobot.on(InteropActions.ChildReady, this.interopDomain, (event: any) => {
                const data = event && event.data;
                const stateName = data && typeof data.stateName === 'string' ? data.stateName : null;
                if (!stateName) {
                    console.warn('[Interop] ChildReady received with no stateName', data);
                    return;
                }
                this.markContextReady(stateName, event && event.source);
            });

            // Pull counterpart to ApplyParamsToContext: a child that boots (or
            // re-enters its route) asks for whatever the host has buffered for its
            // context. This makes delivery self-healing — the child no longer needs
            // the host's push to have arrived at exactly the right moment.
            postRobot.on(InteropActions.GetContextParams, this.interopDomain, (event: any) => {
                const data = event && event.data;
                const stateName = data && typeof data.stateName === 'string' ? data.stateName : null;
                if (!stateName) {
                    console.warn('[Interop] GetContextParams received with no stateName', data);
                    return null;
                }
                // Treat the request as a readiness announcement too: a child that can
                // ask is a child that is listening.
                this.markContextReady(stateName, event && event.source);
                const params = this.contextParamsBuffer[stateName];
                return params ? { name: stateName, params: params } : null;
            });

            // Console-accessible delivery snapshot. Host→child param handoff spans
            // two documents and post-robot, so without this the only way to tell
            // which leg failed is to add logging and redeploy.
            (window as any).__meevoInteropDiag = () => this.diagnostics();
        }

        updateInteropRoute(id, route) {
            console.debug(`updateInteropRoute: id=${id} - route=${route}`,);
            const iframeElement = document.getElementById(id) as HTMLIFrameElement;
            if (iframeElement) {
                this.registerWindow(id, iframeElement.contentWindow);
                const win = this.registeredWindows.find(w => w.id === id);
                postRobot.send(win.window, InteropActions.NavigateTo, route, this.interopDomainUrl)
                    .then(event => {
                        console.debug('[Meevo] send: ', event.data.message)
                    })
                    .catch(error => {
                        console.warn('Meevo Error', error.stack)
                    });
            }
        }

        registerWindow(id: string, window: Window) {
            const existingIndex = this.registeredWindows.findIndex(w => w.id === id);
            if (existingIndex > -1) {
                this.registeredWindows.splice(existingIndex, 1, { id: id, window: window });
            } else {
                this.registeredWindows.push({ id: id, window: window });
            }
            // Readiness is invalidated only when the window we register is a
            // DIFFERENT Window than the one that announced readiness — that is the
            // real signal that the iframe was replaced and the prior child's
            // post-robot listeners are gone. Registering the same live contentWindow
            // (the normal case: registerIframe's deferred lookup, or
            // updateInteropRoute firing from the interop controller's $watch) must
            // preserve it. #121880: comparing against the previously *registered*
            // window instead meant the first registration always invalidated,
            // because there was no previous entry to compare with.
            const readyWindow = this.readyWindows[id];
            if (readyWindow && readyWindow !== window) {
                delete this.readyWindows[id];
            }
        }

        removeWindow(id: string) {
            let index = this.registeredWindows.findIndex(win => win.id === id);
            if (index < 0) { return }
            this.registeredWindows.splice(index, 1);
            // Context closed — drop readiness and buffered params so stale values
            // don't surface if the user opens the same feature later via a
            // different entry point.
            delete this.readyWindows[id];
            delete this.contextParamsBuffer[id];
            delete this.queryParamsBuffer[id];
        }

        /**
         * Push host-provided context params to an interop iframe child app.
         *
         * Use this from AngularJS plugins (e.g. web-component bridges) whenever the
         * host needs to hand off data to an iframe-hosted Angular child — date
         * selection, filter state, pre-selected entities, or anything else the child
         * needs on its next init.
         *
         * Deterministic delivery (no timeouts, no retries):
         *  - **Sticky buffering**: params are cached per stateName and persist until
         *    the tab closes (or clearContextParams is called explicitly). Safe to
         *    call before the iframe even exists.
         *  - **Handshake-gated**: params are actually transmitted only after the
         *    child iframe emits `mi:ChildReady` for the matching stateName. The
         *    child sends that signal from its post-robot bootstrap, which guarantees
         *    its listeners are registered. No race, no arbitrary waits.
         *  - **Replay on re-mount**: if the iframe unmounts and re-mounts (same id),
         *    the child re-emits ChildReady and the buffered params replay.
         *  - **Merge by default**: repeated calls merge into the buffer; pass
         *    `{ replace: true }` to overwrite.
         *
         * On the xplat side, consume via `HostContextParamsService.paramsFor$<T>(name)`.
         * Simply injecting that service from the feature's page component is enough
         * to complete the handshake — the service announces readiness on construction.
         *
         * @param stateName  The host state name of the target iframe context. Must
         *                   match the `id` used by the interop controller when
         *                   registering the iframe window (typically `ROUTE_NAMES.<feature>`).
         * @param params     A serializable record of params. Keep it simple — primitives,
         *                   arrays, plain objects. Anything post-robot can clone.
         * @param options    `{ replace?: boolean }`. Default merges with any sticky
         *                   buffer; `replace: true` overwrites.
         *
         * @example Host plugin (AngularJS) — forwarding a web-component event:
         * ```ts
         * wcElement.addEventListener('scheduleEmployeeClick', (event: Event) => {
         *     const detail = (event as CustomEvent<{ date: string }>).detail;
         *     interopService.pushParamsToContext(
         *         appSettings.ROUTE_NAMES.schedulesManager,
         *         { currentDate: detail.date },
         *     );
         *     $rootScope.$broadcast(Messages.GOTO_STATE, {
         *         stateName: appSettings.ROUTE_NAMES.schedulesManager,
         *     });
         * });
         * ```
         *
         * @example Xplat feature (Angular) — consuming the params:
         * ```ts
         * constructor(private hostParams: HostContextParamsService, private store$: Store) {
         *     this.hostParams.paramsFor$<{ currentDate?: string }>('schedule-manager')
         *         .pipe(takeUntil(this.destroy$))
         *         .subscribe(p => {
         *             if (p.currentDate) {
         *                 this.store$.dispatch(SchedulesActions.setCurrentDate({
         *                     currentDate: new Date(p.currentDate),
         *                 }));
         *             }
         *         });
         * }
         * ```
         */
        pushParamsToContext(stateName: string, params: any, options?: { replace?: boolean }): void {
            if (!stateName || !params || typeof params !== 'object') {
                console.warn('[Interop] pushParamsToContext called with invalid args', { stateName, params });
                return;
            }
            const replace = !!(options && options.replace);
            const current = this.contextParamsBuffer[stateName];
            this.contextParamsBuffer[stateName] = replace || !current
                ? { ...params }
                : { ...current, ...params };
            // If the child has already announced readiness we deliver now; otherwise
            // the ChildReady / GetContextParams handler flushes as soon as it fires.
            if (this.readyWindows[stateName]) {
                this.flushParamsToChild(stateName);
            }
        }

        /**
         * Hand params to an interop context, picking the transport that actually
         * works for the context's current state. This is the method feature code
         * should call; `setQueryParams` and `pushParamsToContext` are the two
         * transports underneath it.
         *
         *  - **Context not open** — it is about to be cold-opened, so the params ride
         *    on the iframe URL. The child reads them synchronously on boot, which
         *    needs no handshake and cannot race.
         *  - **Context already open** — `GOTO_STATE` only selects the existing tab.
         *    No iframe is created and no URL is rebuilt, so a querystring would never
         *    be consumed; it would just sit in the buffer and risk replaying on a
         *    later re-mount. Push over post-robot instead.
         *
         * Both paths stamp the params with a monotonically increasing `seq` so the
         * child can tell a fresh request from a repeat of one it already honoured —
         * the same request legitimately arrives twice when the child also pulls via
         * `GetContextParams`.
         *
         * @example
         * ```ts
         * interopService.sendContextParams(appSettings.ROUTE_NAMES.schedulesManager, {
         *     currentDate: '2026-04-22',
         * });
         * $rootScope.$broadcast(Messages.GOTO_STATE, { stateName: schedulesRoute });
         * ```
         */
        sendContextParams(stateName: string, params: { [key: string]: any }): void {
            if (!stateName || !params || typeof params !== 'object') {
                console.warn('[Interop] sendContextParams called with invalid args', { stateName, params });
                return;
            }
            const stamped = { ...params, seq: ++this.contextParamsSeq };
            if (!this.isContextOpen(stateName)) {
                this.setQueryParams(stateName, this.toQueryStringValues(stamped));
            }
            this.pushParamsToContext(stateName, stamped, { replace: true });
        }

        private toQueryStringValues(params: { [key: string]: any }): Record<string, string> {
            const result: Record<string, string> = {};
            for (const key in params) {
                if (params.hasOwnProperty(key)) {
                    const value = params[key];
                    if (value !== null && value !== undefined) {
                        result[key] = String(value);
                    }
                }
            }
            return result;
        }

        /**
         * True when an iframe is currently mounted for the given interop state name.
         *
         * Callers use this to pick a transport: a context that is NOT open will be
         * cold-opened, so querystring params on the iframe URL are the reliable
         * choice; a context that IS open keeps its iframe (GOTO_STATE only selects
         * the existing tab, so the URL is never rebuilt) and must be updated via
         * pushParamsToContext instead. Staging querystring params for an already-open
         * context leaves them unconsumed and liable to replay on a later re-mount.
         */
        isContextOpen(stateName: string): boolean {
            return !!stateName && this.registeredWindows.some(w => w.id === stateName);
        }

        /**
         * Snapshot of the interop delivery state, for diagnosing "the child never got
         * the params" reports without another build/deploy round trip. Exposed on
         * `window.__meevoInteropDiag()`; safe to call from the browser console.
         */
        diagnostics(): any {
            return {
                registeredWindows: this.registeredWindows.map(w => w.id),
                readyContexts: Object.keys(this.readyWindows),
                readyWindowMatchesRegistered: this.registeredWindows.map(w => ({
                    id: w.id,
                    matches: this.readyWindows[w.id] === w.window,
                })),
                contextParamsBuffer: angular.copy(this.contextParamsBuffer),
                queryParamsBuffer: angular.copy(this.queryParamsBuffer),
            };
        }

        /**
         * Explicitly drop the sticky params buffer for a given interop state.
         *
         * Call this when the host knows the buffered params are no longer relevant
         * (e.g. user logged out, tenant switched, or a different entry-point should
         * get a clean slate). Closing the iframe via removeWindow() clears
         * automatically, so most callers won't need this.
         */
        clearContextParams(stateName: string): void {
            delete this.contextParamsBuffer[stateName];
        }

        /**
         * Stage querystring key-value pairs that will be appended to the iframe URL
         * the next time InteropController creates/refreshes the iframe for the given
         * interop state name. Replaces any previously staged params for that state.
         *
         * This is the cold-open transport: the date (or other initial params) rides
         * on the URL itself so the child can read it synchronously on boot — no
         * post-robot handshake required.
         *
         * For live updates to an already-running iframe, use pushParamsToContext()
         * instead (the warm-redirect transport).
         *
         * @example
         * ```ts
         * interopService.setQueryParams('schedule-manager', { currentDate: '2026-04-22' });
         * interopService.pushParamsToContext('schedule-manager', { currentDate: '2026-04-22' });
         * $rootScope.$broadcast(GOTO_STATE, { stateName: 'schedule-manager' });
         * ```
         */
        setQueryParams(stateName: string, params: Record<string, string>): void {
            if (!stateName || !params || typeof params !== 'object') {
                console.warn('[Interop] setQueryParams called with invalid args', { stateName, params });
                return;
            }
            this.queryParamsBuffer[stateName] = { ...params };
        }

        /**
         * Return and clear the staged querystring params for a given state name.
         * Called by InteropController.updateTargetRoute() when constructing the
         * iframe URL. Returns null when no params are staged — the caller should
         * skip appending a querystring in that case.
         *
         * The consume-and-clear semantic prevents stale params from replaying on
         * subsequent iframe re-mounts when no new params were provided.
         */
        consumeQueryParams(stateName: string): Record<string, string> | null {
            const params = this.queryParamsBuffer[stateName];
            if (!params) { return null; }
            delete this.queryParamsBuffer[stateName];
            return params;
        }

        /**
         * Internal: record the child window that announced readiness and flush any
         * queued params. Invoked by the `mi:ChildReady` and `mi:GetContextParams`
         * post-robot listeners registered in the constructor. Idempotent — a repeat
         * announcement from the same window is a no-op beyond the flush, which is
         * what lets the child safely re-announce whenever it suspects the host lost
         * track of it.
         */
        private markContextReady(stateName: string, sourceWindow?: Window): void {
            if (!stateName) { return; }
            const target = sourceWindow
                || this.readyWindows[stateName]
                || (this.registeredWindows.find(w => w.id === stateName) || { window: null }).window;
            if (!target) {
                console.warn(`[Interop] ChildReady(${stateName}) had no resolvable source window`);
                return;
            }
            this.readyWindows[stateName] = target;
            if (this.contextParamsBuffer[stateName]) {
                this.flushParamsToChild(stateName);
            }
        }

        private flushParamsToChild(stateName: string): void {
            const params = this.contextParamsBuffer[stateName];
            if (!params) { return; }
            // Prefer the window that actually announced readiness. Falling back to
            // registeredWindows keeps older call sites working, but the ready window
            // is what removes registerIframe's deferred lookup from the delivery
            // path entirely.
            const registered = this.registeredWindows.find(w => w.id === stateName);
            const target = this.readyWindows[stateName] || null;
            if (!target) { return; }
            if (registered && registered.window !== target) {
                // The registered iframe is not the one that announced readiness —
                // the child was replaced and has not re-announced yet. Keep the
                // params buffered; the new child's ChildReady / GetContextParams
                // will flush them.
                return;
            }

            postRobot
                .send(
                    target,
                    InteropActions.ApplyParamsToContext,
                    { name: stateName, params },
                    this.interopDomain,
                )
                .catch((err: any) => {
                    console.warn(`[Interop] pushParamsToContext(${stateName}) send failed`, err);
                });
        }

        async pushToAllChild(event: string, payload: any) {
            for (const registeredWindow of this.registeredWindows) {
                try {
                    await postRobot.send(registeredWindow.window, event, payload, this.interopDomain);
                } catch (e) {
                    this.removeWindow(registeredWindow.id);
                }
            }
        }

        /**
         * Broadcast the lockout keep-alive message.
         * Used by webComponentBridge to delegate lockout timer refresh.
         */
        refreshLockoutLastEventTime() {
            this.$rootScope.$broadcast(Meevo.Components.Common.Messages.REFRESH_LOCKOUT_LAST_EVENT_TIME);
        }

        /**
         * Live-refresh the business-info settings after a change was saved elsewhere
         * (e.g. a Meevo shell v2 settings screen). There are two independent caches of
         * the tenant/location settings, both sourced from api/Tenant/BusinessInfo/Settings:
         *   1. BusinessInfoDataService.businessInfoPreferences — the frozen copy the
         *      appointment book mount gate reads to decide legacy vs. enhanced.
         *   2. appSettings.APP_BUSINFOSETTINGS — the copy the new-experience banner's
         *      isOffered() and many other UIs read.
         * getBusinessInfoPreferences({force:true}) re-pulls and refreshes (1); we clone
         * that same fresh payload into (2) as an UNfrozen copy so existing mutators of
         * APP_BUSINFOSETTINGS keep working. A digest then lets the banner's ng-if
         * re-evaluate immediately — no logout/login required.
         */
        private refreshBusinessInfoSettings(): ng.IPromise<void> {
            return this.businessInfoDataService.getBusinessInfoPreferences({ force: true } as any).then((fresh: any) => {
                var rootScope = this.$rootScope as any;
                this.appSettings.APP_BUSINFOSETTINGS = angular.copy(fresh);
                rootScope.appointmentBookSettings = this.appSettings.APP_BUSINFOSETTINGS.appointmentBookSettings;
                rootScope.clientsSettings = this.appSettings.APP_BUSINFOSETTINGS.clientsSettings;
                if (!rootScope.$$phase) {
                    rootScope.$applyAsync();
                }
                rootScope.$broadcast(Meevo.Components.Common.Messages.BUSINESS_INFO_SETTINGS_REFRESHED);
            });
        }

        /**
         * Assemble the canonical host runtime context for web components.
         * This is the single source of truth for auth, session, and app settings
         * that all embedded web components receive via the bridge.
         */
        async getHostRuntimeContext() {
            const resolvedSecurityKeys = await Promise.resolve(this.appSettings.SECURITY_ACCESS_KEYS);
            const securityItems = Array.isArray(resolvedSecurityKeys) ? resolvedSecurityKeys : [];
            const interopAppSettings = this.getAppSettings();

            return {
                apiBaseUrl: `/${this.appSettings.API_ROOT}`,
                session: this.getSession(),
                appSettings: {
                    API_ROOT: this.appSettings.API_ROOT,
                    APP_COUNTRYID: this.appSettings.APP_COUNTRYID,
                    APP_LANGUAGEID: this.appSettings.APP_LANGUAGEID,
                    APP_TIME_FORMAT: this.appSettings.APP_TIME_FORMAT,
                    APP_ISOCOUNTRYCODE: interopAppSettings.APP_ISOCOUNTRYCODE,
                    APP_FIELDLABELS: interopAppSettings.APP_FIELDLABELS,
                    APP_BUSINFOSETTINGS: this.appSettings.APP_BUSINFOSETTINGS,
                    SECURITY_ACCESS_KEYS: interopAppSettings.SECURITY_ACCESS_KEYS !== undefined && interopAppSettings.SECURITY_ACCESS_KEYS !== null
                        ? interopAppSettings.SECURITY_ACCESS_KEYS
                        : securityItems,
                },
                securityItems,
            };
        }

        private getSession(): Meevo.Components.Common.IIdentity {
            let session = this.identityService.current();
            const win = window as any;
            if (win.bearerToken) {
                session = { ...session, ...{ bearerToken: win.bearerToken, localeId: this.appSettings.APP_COUNTRYID } };
            }
            return session;
        }

        private getAppSettings() {
            let appSettings = {
                APP_FIELDLABELS: this.appSettings.APP_FIELDLABELS,
                SECURITY_ACCESS_KEYS: this.appSettings.SECURITY_ACCESS_KEYS,
                APP_ISOCOUNTRYCODE: this.appSettings.APP_ISOCOUNTRYCODE
            };
            return appSettings;
        }

        private getWalkmeAppSettings() {
            let walkmeUrl = "";
            if (window["WalkMeUrl_ME"] && !window['pendo']) {
                walkmeUrl = window["WalkMeUrl_ME"];
                console.log(window["WalkMeUrl_ME"]);
            }

            let walkMeAppSettings = { WALKME_URL: walkmeUrl };
            return walkMeAppSettings;
        }

        private showAuditHistoryModal(auditLog) {
            let options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: 'app/components/auditHistory/auditHistory.html',
                locals: {
                    auditLog,
                    userId: this.identityService.current().userId,
                    userName: this.identityService.current().userAuthName
                },
                controller: 'meevo.auditHistory.AuditHistoryController',
                controllerAs: 'auditHistory',
                bindToController: true
            };

            this.modalService.showCustom(options);
        }

        private getTippyServiceUrl() {
            return { tippyUrl: window['TippyMicroserviceUrl'] };
        }

        private getPendoData() {
            return window['pendo'] && window['pendo'].getSerializedMetadata ? 
                window['pendo'].getSerializedMetadata() :
                null;
        }

        static factory(): Function {
            let svc = ($cookies: angular.cookies.ICookiesService, appSettings: Meevo.Components.Common.IAppSettings, identityService: Meevo.Components.Common.IIdentityService, modalService: Meevo.Components.Modal.IModalService, $rootScope: angular.IRootScopeService, $state: angular.ui.IStateService, docsAndFormsInteropService: DocsAndFormsInteropService,
                busyService: Meevo.Components.Busy.IBusyService, $translate: angular.translate.ITranslateService, businessInfoDataService: Meevo.Components.Common.IBusinessInfoDataService) => {
                return new InteropService($cookies, appSettings, identityService, modalService, $rootScope, $state, docsAndFormsInteropService,
                    busyService, $translate, businessInfoDataService);
            }

            svc.$inject = ['$cookies', 'meevo.AppSettings', 'meevo.components.common.IdentityService', 'meevo.components.modal.ModalService', '$rootScope', '$state', 'meevo.plugins.docsAndForms.DocsAndFormsInteropService',
                'meevo.components.busy.BusyService', "$translate", 'meevo.components.common.BusinessInfoDataService'];
            return svc;
        }
    }

    angular.module('meevo.plugins.interop')
        .factory('meevo.plugins.interop.InteropService', InteropService.factory());
}
